import React, { useEffect, useState } from "react";
import Modal from "@material-ui/core/Modal";
import Backdrop from "@material-ui/core/Backdrop";
import Fade from "@material-ui/core/Fade";
import Button from "@material-ui/core/Button";
import { useTranslation } from "react-i18next";
import Webcam from "react-webcam";
import styles from "./styles";

interface CameraCaptureModalProps {
  open: boolean;
  onClose: () => void;
  onUseCapturedImage: (dataUrl: string) => void;
}

const CameraCaptureModal = React.memo(
  (props: CameraCaptureModalProps): React.ReactElement | null => {
    const { open, onClose, onUseCapturedImage } = props;

    const classes = styles();
    const translate = useTranslation().t;
    const [capturedImage, setCapturedImage] = useState<string | null>(null);
    const webcamRef = React.useRef<Webcam>(null);

    useEffect(() => {
      if (!open) setCapturedImage(null);
    }, [open]);

    const handleCapture = () => {
      if (!webcamRef.current) return;

      const imageSrc = webcamRef.current.getScreenshot({
        width: 1280,
        height: 720,
      });

      if (!imageSrc) return;

      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");
        if (!ctx) return;
        canvas.width = img.width;
        canvas.height = img.height;
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = "high";
        ctx.translate(canvas.width, 0);
        ctx.scale(-1, 1);
        ctx.drawImage(img, 0, 0);
        const flippedImageSrc = canvas.toDataURL("image/jpeg", 0.98);
        setCapturedImage(flippedImageSrc);
      };
      img.src = imageSrc;
    };

    const handleRetake = () => {
      setCapturedImage(null);
    };

    const handleUseCapturedImage = () => {
      if (!capturedImage) return;
      onUseCapturedImage(capturedImage);
    };

    return (
      <Modal
        className={classes.modal}
        open={open}
        onClose={onClose}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{ timeout: 500 }}
      >
        <Fade in={open}>
          <div className={classes.modalPaper}>
            <h2 className={classes.modalTitle}>{translate("TAKE_PHOTO")}</h2>

            {!capturedImage ? (
              <>
                <div className={classes.cameraPreview}>
                  <Webcam
                    audio={false}
                    ref={webcamRef}
                    screenshotFormat="image/jpeg"
                    screenshotQuality={1}
                    videoConstraints={{
                      facingMode: "user",
                      width: { ideal: 1280 },
                      height: { ideal: 720 },
                    }}
                    className={classes.webcamElement}
                  />
                </div>

                <div className={classes.modalActions}>
                  <Button onClick={onClose} variant="outlined">
                    {translate("CANCEL")}
                  </Button>
                  <Button
                    onClick={handleCapture}
                    variant="contained"
                    className={classes.saveButton}
                  >
                    {translate("CAPTURE")}
                  </Button>
                </div>
              </>
            ) : (
              <>
                <div className={classes.previewContainer}>
                  <img
                    src={capturedImage}
                    alt="Captured"
                    className={classes.capturedImage}
                  />
                </div>

                <div className={classes.modalActions}>
                  <Button onClick={handleRetake} variant="outlined">
                    {translate("RETAKE")}
                  </Button>
                  <Button
                    onClick={handleUseCapturedImage}
                    variant="contained"
                    className={classes.saveButton}
                  >
                    {translate("USE_THIS_PHOTO")}
                  </Button>
                </div>
              </>
            )}
          </div>
        </Fade>
      </Modal>
    );
  }
);

export default CameraCaptureModal;

